/**
 * BỘ ICON DUY NHẤT của app — nét tròn, đầu nét bo (Lucide), thay cho bộ Outlined góc vuông của Ant Design
 * (chủ dự án 22/09/2026: «icon mềm hơn»). Tên xuất ra GIỮ NGUYÊN tên cũ (`DeleteOutlined`…) để đổi bộ icon
 * không phải sửa 50 màn hình — chỉ đổi dòng import. Đây là file DUY NHẤT được nhập `lucide-react`
 * (`source-hygiene.test.ts` chặn mọi chỗ khác, kể cả `@ant-design/icons`).
 *
 * Mỗi icon là một `<span class="anticon">` bọc SVG cỡ `1em`, giống hệt cách icon Ant Design tự dựng, nên
 * mọi thành phần Ant Design (nút, menu, thẻ, ô nhập…) căn chỉnh và tô màu chúng như icon của chính nó, và
 * bộ soát thiết kế (`scripts/design-audit.ts`) vẫn đo được tương phản icon.
 */
import {
  ArrowDown,
  ArrowLeft,
  ArrowLeftRight,
  ArrowRight,
  ArrowUp,
  AudioLines,
  Bell,
  BellOff,
  Bot,
  Brush,
  CalendarDays,
  ChartColumn,
  ChartLine,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronsRight,
  CircleAlert,
  CircleCheck,
  CircleDollarSign,
  CircleMinus,
  CircleQuestionMark,
  CircleX,
  ClipboardCheck,
  ClipboardList,
  ClipboardPaste,
  Clock,
  Copy,
  Diamond,
  Download,
  ExternalLink,
  Eye,
  EyeOff,
  FileSearch,
  FileText,
  Flame,
  FlipHorizontal,
  Folder,
  FolderOpen,
  FolderPlus,
  GripVertical,
  Headphones,
  Heart,
  History,
  IdCard,
  Image,
  KeyRound,
  Layers,
  LayoutGrid,
  Link,
  List,
  ListChecks,
  ListFilter,
  Lock,
  MonitorCog,
  LogIn,
  LogOut,
  Menu,
  MessageCircle,
  Minus,
  Moon,
  Network,
  Newspaper,
  Pencil,
  Pin,
  Play,
  Plus,
  Power,
  Printer,
  Radar,
  Redo2,
  RefreshCcw,
  RefreshCw,
  Save,
  Search,
  Send,
  Settings,
  Settings2,
  Smartphone,
  SquareKanban,
  SquarePlay,
  Star,
  Sun,
  ThumbsUp,
  Trash,
  TrendingUp,
  TriangleAlert,
  Trophy,
  TvMinimalPlay,
  Type,
  Undo2,
  Upload,
  User,
  UserPlus,
  Users,
  Volume2,
  Wallet,
  Wifi,
  WifiOff,
  X,
  type LucideIcon,
} from 'lucide-react';
import { forwardRef, type HTMLAttributes } from 'react';

/** Nhận mọi thuộc tính của `<span>` (style, aria-label, tabIndex, onClick…) như icon Ant Design. */
export interface IconProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** Quay tròn — đang tải, đang đồng bộ. */
  spin?: boolean;
}

/** Nét 2/24 = ~1,2px ở cỡ 14px: ngang nét icon Ant Design cũ nên độ đậm của cả trang không đổi. */
const STROKE_WIDTH = 2;

/** `filled`: tô đặc bằng màu chữ (ngôi sao, kim cương đánh dấu chức vụ) thay vì chỉ vẽ nét. */
function make(name: string, Glyph: LucideIcon, filled = false) {
  const Icon = forwardRef<HTMLSpanElement, IconProps>(function Icon(
    { className, style, spin, ...rest },
    ref,
  ) {
    const classes = ['anticon', `anticon-${name}`, 'halo-icon'];
    if (spin) classes.push('halo-icon-spin');
    if (className) classes.push(className);
    return (
      <span
        ref={ref}
        role="img"
        aria-label={name}
        {...rest}
        className={classes.join(' ')}
        style={style}
      >
        <Glyph
          size="1em"
          strokeWidth={STROKE_WIDTH}
          fill={filled ? 'currentColor' : 'none'}
          aria-hidden
          focusable={false}
        />
      </span>
    );
  });
  Icon.displayName = `Icon(${name})`;
  return Icon;
}

// ---------------------------------------------------------------- mục lục (app/navigation.tsx)
export const AppstoreOutlined = make('appstore', LayoutGrid);
/** Bày dạng danh sách (đối lại với lưới ô). */
export const BarsOutlined = make('bars', List);
export const PlaySquareOutlined = make('play-square', SquarePlay);
export const BarChartOutlined = make('bar-chart', ChartColumn);
export const SlidersOutlined = make('sliders', AudioLines);
export const CustomerServiceOutlined = make('customer-service', Headphones);
export const IdcardOutlined = make('idcard', IdCard);
export const FundOutlined = make('fund', Wallet);
export const FileSearchOutlined = make('file-search', FileSearch);
export const FileTextOutlined = make('file-text', FileText);
export const EditOutlined = make('edit', Pencil);
export const YoutubeOutlined = make('youtube', TvMinimalPlay);
export const CheckSquareOutlined = make('check-square', ListChecks);
/** Dấu tích trần — ô tích tự vẽ trên thẻ ảnh («Tải thumbnail đối thủ»). */
export const CheckOutlined = make('check', Check);
export const ProfileOutlined = make('profile', ClipboardList);
export const ProjectOutlined = make('project', SquareKanban);
export const RadarChartOutlined = make('radar-chart', Radar);
export const LineChartOutlined = make('line-chart', ChartLine);
export const SearchOutlined = make('search', Search);
export const ControlOutlined = make('control', Settings2);
export const ApartmentOutlined = make('apartment', Network);
export const FolderOpenOutlined = make('folder-open', FolderOpen);
export const SettingOutlined = make('setting', Settings);

// ---------------------------------------------------------------- thao tác
export const PlusOutlined = make('plus', Plus);
export const MinusOutlined = make('minus', Minus);
export const DeleteOutlined = make('delete', Trash);
export const SaveOutlined = make('save', Save);
export const CopyOutlined = make('copy', Copy);
export const ReloadOutlined = make('reload', RefreshCw);
export const SyncOutlined = make('sync', RefreshCcw);
export const UploadOutlined = make('upload', Upload);
export const DownloadOutlined = make('download', Download);
/** Điện thoại — mục «Cài ứng dụng» (PWA). */
export const MobileOutlined = make('mobile', Smartphone);
export const ExportOutlined = make('export', ExternalLink);
export const LinkOutlined = make('link', Link);
export const FilterOutlined = make('filter', ListFilter);
export const SwapOutlined = make('swap', ArrowLeftRight);
export const SnippetsOutlined = make('snippets', ClipboardPaste);
export const PushpinOutlined = make('pushpin', Pin);
export const PoweroffOutlined = make('poweroff', Power);
/** Quản lý GPM (11/10/2026): máy cài GPM có cầu nối. */
export const DesktopOutlined = make('desktop', MonitorCog);
/** Hiện / che mật khẩu trong mục Lưu trữ. */
export const EyeOutlined = make('eye', Eye);
export const EyeInvisibleOutlined = make('eye-invisible', EyeOff);
export const KeyOutlined = make('key', KeyRound);
export const LockOutlined = make('lock', Lock);
export const CloseOutlined = make('close', X);
export const MenuOutlined = make('menu', Menu);
/** Bảng tin (03/10/2026): thích · tim · bình luận · gửi · tin tức. */
export const LikeOutlined = make('like', ThumbsUp);
export const LikeFilled = make('like-filled', ThumbsUp, true);
export const HeartOutlined = make('heart', Heart);
export const HeartFilled = make('heart-filled', Heart, true);
export const CommentOutlined = make('comment', MessageCircle);
export const SendOutlined = make('send', Send);
export const NewspaperOutlined = make('newspaper', Newspaper);
/** Tay cầm kéo-thả (sắp xếp dòng). */
export const HolderOutlined = make('holder', GripVertical);
export const LogoutOutlined = make('logout', LogOut);
export const UserOutlined = make('user', User);
export const TeamOutlined = make('team', Users);
export const UserAddOutlined = make('user-add', UserPlus);
export const FolderOutlined = make('folder', Folder);
export const FolderAddOutlined = make('folder-add', FolderPlus);
export const PictureOutlined = make('picture', Image);
export const CaretRightOutlined = make('caret-right', Play);
export const SoundOutlined = make('sound', Volume2);
export const RobotOutlined = make('robot', Bot);
export const BellOutlined = make('bell', Bell);
/** Chuông gạch chéo — mục «Tắt thông báo» và trạng thái thông báo bị chặn. */
export const BellOffOutlined = make('bell-off', BellOff);
export const MoonOutlined = make('moon', Moon);
export const SunOutlined = make('sun', Sun);

// ---------------------------------------------------------------- trạng thái, chiều
export const CheckCircleOutlined = make('check-circle', CircleCheck);
export const CloseCircleOutlined = make('close-circle', CircleX);
export const MinusCircleOutlined = make('minus-circle', CircleMinus);
export const ClockCircleOutlined = make('clock-circle', Clock);
/** Đơn từ (05/10/2026): mục «Đơn từ» trong menu. */
export const AuditOutlined = make('audit', ClipboardCheck);
/** Đơn xin nghỉ phép. */
export const CalendarOutlined = make('calendar', CalendarDays);
/** In phiếu đề xuất. */
export const PrinterOutlined = make('printer', Printer);
/** Lịch sử truy cập — đồng hồ có mũi tên quay lui. */
export const HistoryOutlined = make('history', History);
export const WarningOutlined = make('warning', TriangleAlert);
/** Đánh dấu Trưởng phòng / Trưởng nhóm trên sơ đồ bộ máy — tô đặc. */
export const StarFilled = make('star', Star, true);
export const DiamondFilled = make('diamond', Diamond, true);
export const QuestionCircleOutlined = make('question-circle', CircleQuestionMark);
/** Màn chấm công (05/10/2026, ảnh mẫu): điều kiện chưa đạt · giờ vào làm · mạng công ty. */
export const ExclamationCircleOutlined = make('exclamation-circle', CircleAlert);
export const LoginOutlined = make('login', LogIn);
export const WifiOutlined = make('wifi', Wifi);
/** Ngoài Wi-Fi công ty — nút chấm công khoá (bản bàn giao 07/10/2026). */
export const WifiOffOutlined = make('wifi-off', WifiOff);
export const FireOutlined = make('fire', Flame);
export const TrophyOutlined = make('trophy', Trophy);
export const TrendingOutlined = make('trending', TrendingUp);
export const ArrowUpOutlined = make('arrow-up', ArrowUp);
export const ArrowDownOutlined = make('arrow-down', ArrowDown);
export const ArrowLeftOutlined = make('arrow-left', ArrowLeft);
export const ArrowRightOutlined = make('arrow-right', ArrowRight);
export const UpOutlined = make('up', ChevronUp);
export const DownOutlined = make('down', ChevronDown);
export const LeftOutlined = make('left', ChevronLeft);
export const RightOutlined = make('right', ChevronRight);
export const VerticalLeftOutlined = make('vertical-left', ChevronsRight);
export const BrushOutlined = make('brush', Brush);
export const FontSizeOutlined = make('font-size', Type);
export const UndoOutlined = make('undo', Undo2);
export const RedoOutlined = make('redo', Redo2);
export const FlipOutlined = make('flip', FlipHorizontal);
export const LayersOutlined = make('layers', Layers);
export const DollarOutlined = make('dollar', CircleDollarSign);
